import { clsx } from 'clsx';
import { useMemo } from 'react';
import Select from 'react-select';
import { ScheduleTypeEnum } from '../../../../../../js/api';
import { useCodeDefinitionsAsOptions } from '../../../../hooks/useCodeDefinitions';
import { getReactSelectCompactStyles } from '../../../../utils/utils';

interface ScheduleTypeSelectProps {
  value: ScheduleTypeEnum;
  onChange: (next: ScheduleTypeEnum) => void;
  disabled?: boolean;
  className?: string;
}

type Option = { value: ScheduleTypeEnum; label: string; is_active?: boolean };

// Memoize the styles object so it isn't recomputed each render — react-select
// shallow-compares and re-styles when the styles prop changes identity.
const COMPACT_STYLES = getReactSelectCompactStyles();

export const ScheduleTypeSelect = ({
  value,
  onChange,
  disabled,
  className,
}: ScheduleTypeSelectProps) => {
  // `ScheduleType` is registered as a CodeDefinition category by
  // `load_code_definitions` — see backend/dimensions/utils.py. Loading from
  // the cached store means labels stay in lockstep with the backend enum
  // without us having to mirror them in the frontend.
  const { data: options } = useCodeDefinitionsAsOptions<ScheduleTypeEnum>('ScheduleType');
  const selectedOption = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  return (
    // `tw:w-fit` lets the control size to the widest option label so the
    // selected value never truncates. Compact styles set
    // `whiteSpace: nowrap` on singleValue so the label stays on one line.
    <div className={clsx('tw:w-fit', className)}>
      <Select<Option>
        id="provider-schedule-type"
        options={options}
        value={selectedOption}
        onChange={(option) => option && onChange(option.value)}
        placeholder="Select schedule type..."
        styles={COMPACT_STYLES}
        isClearable={false}
        // 3 fixed options — no need for typeahead, and dropping the
        // search input is a defense-in-depth measure against the iOS
        // Safari auto-zoom that triggers on any focused <16px input.
        isSearchable={false}
        isDisabled={disabled || options.length === 0}
      />
    </div>
  );
};
